import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Users, Clock, CheckCircle, XCircle, Trash2, Plus } from 'lucide-react';
import { ref as dbRef, onValue, set, update, push } from 'firebase/database';
import { db } from '../firebase';
import ScheduleBookingModal from '../pages/modals/ScheduleBookingModal';

export const SchedulesListPage = ({ currentUser, showToast, isAdmin = false }) => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!db) {
      console.error('Database not initialized');
      setLoading(false);
      return;
    }

    const schedulesRef = dbRef(db, 'schedules');
    let unsubscribe;

    const setupListener = async () => {
      try {
        unsubscribe = onValue(schedulesRef, (snapshot) => {
          const data = snapshot.val();
          
          if (data) {
            let schedulesList = Object.entries(data).map(([key, value]) => ({
              id: key,
              ...value
            }));
            
            if (!isAdmin) {
              schedulesList = schedulesList.filter(s => s.status === 'approved');
            }
            
            setSchedules(schedulesList);
          } else {
            setSchedules([]);
          }
          
          setLoading(false);
        }, (error) => {
          console.error('Error listening to schedules:', error);
          setLoading(false);
        });
      } catch (err) {
        console.error('Error setting up listener:', err);
        setLoading(false);
      }
    };

    setupListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [isAdmin]);

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return 'bg-[#bd9f67]/20 text-[#bd9f67] border-[#bd9f67]/40';
      case 'rejected': return 'bg-red-500/20 text-red-300 border-red-500/40';
      case 'pending': return 'bg-amber-500/20 text-amber-300 border-amber-500/40';
      default: return 'bg-white/10 text-white/70 border-white/20';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'approved': return <CheckCircle size={14} />;
      case 'rejected': return <XCircle size={14} />;
      case 'pending': return <Clock size={14} />;
      default: return null;
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!window.confirm('Are you sure you want to delete this schedule request?')) return;

    try {
      const scheduleRef = dbRef(db, `schedules/${scheduleId}`);
      await set(scheduleRef, null);
      showToast('Schedule deleted successfully', 'success');
    } catch (err) {
      console.error('Error deleting schedule:', err);
      showToast('Error deleting schedule', 'error');
    }
  };

  const handleStatusUpdate = async (scheduleId, newStatus) => {
    try {
      const scheduleRef = dbRef(db, `schedules/${scheduleId}`);
      await update(scheduleRef, { status: newStatus });
      showToast(`Schedule marked as ${newStatus}`, 'success');
    } catch (err) {
      console.error('Error updating schedule:', err);
      showToast('Error updating schedule', 'error');
    }
  };

  const handleCreateSchedule = async (scheduleData) => {
    setIsSubmitting(true);
    try {
      const schedulesRef = dbRef(db, 'schedules');
      const newScheduleRef = push(schedulesRef);
      await set(newScheduleRef, scheduleData);
      showToast('Schedule created successfully!', 'success');
      setShowCreateModal(false);
    } catch (err) {
      console.error('Error creating schedule:', err);
      showToast('Error creating schedule', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredSchedules = isAdmin
    ? (filter === 'all' ? schedules : schedules.filter(s => s.status === filter))
    : schedules;

  return (
    <div className="w-full min-h-screen bg-[#08080a] text-white font-sans antialiased pt-24 pb-16 relative overflow-hidden">
      {/* Background Ambient Gold Glows */}
      <div className="absolute top-20 left-10 w-96 h-96 bg-[#bd9f67]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute top-1/3 right-10 w-80 h-80 bg-[#d0b07a]/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-7xl mx-auto relative z-10 px-4 sm:px-6">
        {/* Header Title */}
        <motion.div
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3.5">
            <div className="p-3 bg-[#bd9f67]/10 border border-[#bd9f67]/20 rounded-2xl flex-shrink-0 text-[#bd9f67]">
              <Calendar size={22} />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-1.5 text-[#bd9f67] text-xs font-semibold tracking-wider uppercase mb-0.5">
              Ministry Events
              </div>
              <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-white">
                {isAdmin ? 'Schedule Requests' : 'Schedules'}
              </h1>
              <p className="text-white/60 text-xs sm:text-sm mt-1">
                {isAdmin 
                  ? 'Manage event booking requests and schedule approvals' 
                  : `${filteredSchedules.length} upcoming ministration${filteredSchedules.length !== 1 ? 's' : ''} scheduled`}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Admin Action Buttons & Filters */}
        {isAdmin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="flex flex-wrap items-center gap-2.5 mb-8"
          >
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold transition text-xs sm:text-sm bg-[#bd9f67] text-gray-950 shadow-lg shadow-[#bd9f67]/20 hover:bg-[#d0b07a]"
            >
              <Plus size={16} />
              Create Schedule
            </motion.button>
            {['all', 'pending', 'approved', 'rejected'].map(status => (
              <motion.button
                key={status}
                whileTap={{ scale: 0.95 }}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-xl font-medium transition text-xs sm:text-sm capitalize ${
                  filter === status
                    ? 'bg-white/15 text-[#bd9f67] border border-[#bd9f67]/40'
                    : 'bg-[#121318] text-white/70 border border-white/10 hover:border-white/20 hover:text-white'
                }`}
              >
                {status}
              </motion.button>
            ))}
          </motion.div>
        )}

        {/* Content Body */}
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block animate-spin mb-3">
              <div className="w-10 h-10 border-2 border-[#bd9f67]/30 border-t-[#bd9f67] rounded-full"></div>
            </div>
            <p className="text-white/50 text-xs tracking-wider uppercase">Loading schedules...</p>
          </div>
        ) : filteredSchedules.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="bg-[#121318]/70 backdrop-blur-xl border border-white/10 rounded-2xl p-8 sm:p-12 text-center max-w-xl mx-auto shadow-2xl"
          >
            <div className="p-3 bg-[#bd9f67]/10 border border-[#bd9f67]/20 rounded-2xl w-fit mx-auto mb-4 text-[#bd9f67]">
              <Calendar size={32} />
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-white mb-2">
              {isAdmin ? 'No Requests Found' : 'No Scheduled Performances'}
            </h3>
            <p className="text-white/60 text-xs sm:text-sm leading-relaxed">
              {isAdmin
                ? filter === 'all'
                  ? 'No event booking requests submitted yet.'
                  : `No ${filter} schedule requests.`
                : 'No performances are currently scheduled. Check back soon for upcoming events!'}
            </p>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className="space-y-4"
          >
            {filteredSchedules.map((schedule, index) => (
              <motion.div
                key={schedule.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.04 }}
                className="bg-[#121318]/80 backdrop-blur-xl border border-white/10 hover:border-[#bd9f67]/40 rounded-2xl p-5 transition-all duration-300 shadow-xl group"
              >
                {/* Header Info */}
                <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-white text-base sm:text-lg tracking-tight truncate">
                      {schedule.eventName}
                    </h3>
                    <p className="text-[#bd9f67] text-xs font-medium mt-0.5 capitalize">
                      {schedule.eventType}
                    </p>
                  </div>
                  {isAdmin && (
                    <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full border text-xs font-semibold flex-shrink-0 ${getStatusColor(schedule.status)}`}>
                      {getStatusIcon(schedule.status)}
                      <span className="capitalize">{schedule.status}</span>
                    </div>
                  )}
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4 pb-4 border-b border-white/5">
                  {/* Start Date */}
                  <div className="flex items-start gap-2.5">
                    <div className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#bd9f67] flex-shrink-0 mt-0.5">
                      <Calendar size={14} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Start Date</p>
                      <p className="text-white/90 text-xs sm:text-sm font-medium mt-0.5">
                        {new Date(schedule.startDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* End Date */}
                  <div className="flex items-start gap-2.5">
                    <div className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#bd9f67] flex-shrink-0 mt-0.5">
                      <Calendar size={14} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">End Date</p>
                      <p className="text-white/90 text-xs sm:text-sm font-medium mt-0.5">
                        {new Date(schedule.endDate).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Location */}
                  <div className="flex items-start gap-2.5">
                    <div className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#bd9f67] flex-shrink-0 mt-0.5">
                      <MapPin size={14} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Location</p>
                      <p className="text-white/90 text-xs sm:text-sm font-medium truncate mt-0.5">{schedule.location}</p>
                    </div>
                  </div>

                  {/* Attendees */}
                  {schedule.attendees && (
                    <div className="flex items-start gap-2.5">
                      <div className="p-1.5 rounded-lg bg-white/5 border border-white/10 text-[#bd9f67] flex-shrink-0 mt-0.5">
                        <Users size={14} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider">Attendees</p>
                        <p className="text-white/90 text-xs sm:text-sm font-medium mt-0.5">{schedule.attendees}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Admin Contact Block */}
                {isAdmin && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4 pb-4 border-b border-white/5 text-xs bg-black/20 p-3 rounded-xl border border-white/5">
                    <div className="min-w-0">
                      <p className="text-white/40 font-semibold mb-0.5">Contact Name</p>
                      <p className="text-white/90 truncate">{schedule.contactName || 'N/A'}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white/40 font-semibold mb-0.5">Email</p>
                      <p className="text-white/90 break-all">{schedule.contactEmail || 'N/A'}</p>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white/40 font-semibold mb-0.5">Phone</p>
                      <p className="text-white/90">{schedule.contactPhone || 'N/A'}</p>
                    </div>
                  </div>
                )}

                {/* Description */}
                {schedule.description && (
                  <div className="mb-4 pb-4 border-b border-white/5">
                    <p className="text-[10px] text-white/40 uppercase font-bold tracking-wider mb-1">Description</p>
                    <p className="text-white/70 text-xs sm:text-sm leading-relaxed line-clamp-2">{schedule.description}</p>
                  </div>
                )}

                {/* Admin Actions Footer */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-1">
                  {isAdmin && (
                    <p className="text-[11px] text-white/40">
                      Requested on {new Date(schedule.createdAt).toLocaleDateString()}
                    </p>
                  )}

                  {isAdmin && (
                    <div className="flex flex-wrap items-center gap-2 ml-auto sm:ml-0">
                      {schedule.status === 'pending' && (
                        <>
                          <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleStatusUpdate(schedule.id, 'approved')}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-[#bd9f67]/15 hover:bg-[#bd9f67]/25 text-[#bd9f67] border border-[#bd9f67]/30 rounded-xl transition text-xs font-semibold"
                          >
                            <CheckCircle size={14} />
                            Approve
                          </motion.button>
                          <motion.button
                            whileTap={{ scale: 0.95 }}
                            onClick={() => handleStatusUpdate(schedule.id, 'rejected')}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/15 hover:bg-red-500/25 text-red-300 border border-red-500/30 rounded-xl transition text-xs font-semibold"
                          >
                            <XCircle size={14} />
                            Reject
                          </motion.button>
                        </>
                      )}
                      <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleDeleteSchedule(schedule.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-xl transition text-xs font-semibold"
                      >
                        <Trash2 size={14} />
                        Delete
                      </motion.button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>

      <ScheduleBookingModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateSchedule}
        isProcessing={isSubmitting}
        currentUser={currentUser}
        isAdmin={isAdmin}
      />
    </div>
  );
};

export default SchedulesListPage;